import { useState, useRef, useEffect, useCallback } from "react";
import { Check } from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";

const OTHER_SENTINEL = "__other__";

export default function EditableCell({
  value,
  onSave,
  type = "text",
  options = [],
  placeholder = "—",
  disabled = false,
  formatDisplay = null,
  allowOther = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  // When the user picks "Other…" inside a select, we flip into a one-shot
  // text-input session so they can type a value not present in the dropdown.
  const [otherMode, setOtherMode] = useState(false);
  const inputRef = useRef(null);
  const confirm = useConfirm();

  // Sync draft when value prop changes externally
  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  // Focus input when entering edit mode (or flipping into Other-mode text input)
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if ((type === "text" || otherMode) && inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [editing, type, otherMode]);

  const handleSave = useCallback(
    async (newValue) => {
      const trimmed = typeof newValue === "string" ? newValue : String(newValue ?? "");
      // Don't save if unchanged
      if (trimmed === (value ?? "")) {
        setEditing(false);
        return;
      }
      // Confirmation step
      const oldDisplay = value || "(empty)";
      const newDisplay = trimmed || "(empty)";
      const ok = await confirm({
        title: "Save this change?",
        description: `Change "${oldDisplay}" to "${newDisplay}"?`,
        confirmText: "Save",
      });
      if (!ok) {
        setDraft(value ?? "");
        setEditing(false);
        return;
      }
      setSaving(true);
      try {
        await onSave(trimmed);
        setDraft(trimmed);
        setEditing(false);
        setOtherMode(false);
      } catch {
        // Revert on error
        setDraft(value ?? "");
        setEditing(false);
        setOtherMode(false);
      } finally {
        setSaving(false);
      }
    },
    [onSave, value, confirm]
  );

  const handleCancel = useCallback(() => {
    setDraft(value ?? "");
    setEditing(false);
    setOtherMode(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSave(draft);
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [draft, handleSave, handleCancel]
  );

  // No auto-save/cancel on blur — use save button or Enter/Escape instead.
  // Blur from clicking the save button must not cancel first.

  // --- Display mode ---
  if (!editing) {
    const display = (formatDisplay && value) ? formatDisplay(value) : (value || placeholder);
    return (
      <span
        className={`inline-block cursor-pointer rounded px-1 py-0.5 min-w-[2rem] ${
          disabled ? "cursor-default" : "hover:bg-gray-100"
        } ${!value ? "text-gray-400 italic" : ""}`}
        onClick={() => {
          if (!disabled) {
            setDraft(value ?? "");
            setOtherMode(false);
            setEditing(true);
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setDraft(value ?? "");
            setOtherMode(false);
            setEditing(true);
          }
        }}
      >
        {display}
      </span>
    );
  }

  // --- Select ---
  if (type === "select" && !otherMode) {
    // When the current value isn't in the canonical list, show it at the top
    // as a disabled placeholder so the user can see what they're replacing.
    const valueInOptions = options.includes(draft);
    return (
      <select
        ref={inputRef}
        value={valueInOptions ? draft : ""}
        onChange={(e) => {
          const next = e.target.value;
          if (next === OTHER_SENTINEL) {
            // Flip into text-input mode for free-form entry.
            setOtherMode(true);
            setDraft("");
            return;
          }
          setDraft(next);
          handleSave(next);
        }}
        onBlur={handleCancel}
        onKeyDown={handleKeyDown}
        disabled={saving}
        className="text-sm border border-gray-300 rounded px-1 py-0.5 bg-white focus:ring-1 focus:ring-blue-400 outline-none"
      >
        {!valueInOptions && (
          <option value="" disabled>
            {draft ? `(${draft})` : "Select…"}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
        {allowOther && (
          <option key={OTHER_SENTINEL} value={OTHER_SENTINEL}>
            Other…
          </option>
        )}
      </select>
    );
  }

  const saveButton = (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSave(draft);
      }}
      disabled={saving}
      className="flex-shrink-0 p-0.5 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
      title="Save"
    >
      <Check className="w-3.5 h-3.5" />
    </button>
  );

  // --- Date ---
  if (type === "date") {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={saving}
          className="text-sm border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-400 outline-none"
        />
        {saveButton}
      </div>
    );
  }

  // --- Number ---
  if (type === "number") {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={saving}
          className="text-sm border border-gray-300 rounded px-1 py-0.5 w-28 focus:ring-1 focus:ring-blue-400 outline-none"
        />
        {saveButton}
      </div>
    );
  }

  // --- Text (default) ---
  return (
    <div className="flex items-center gap-1">
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={saving}
        className="text-sm border border-gray-300 rounded px-1 py-0.5 flex-1 focus:ring-1 focus:ring-blue-400 outline-none"
      />
      {saveButton}
    </div>
  );
}
